import { useState } from 'react'
import { tenantConfig } from '../config/tenant'
import Nav from '../components/Nav'
import Seo from '../components/Seo'
import Footer from '../components/Footer'
import LeadCaptureModal from '../components/LeadCaptureModal'
import { downloadAsset } from '../lib/downloadAsset'

/**
 * Playbooks hub — one page listing our free guides (First Home Buyer +
 * Refinancing). Each card captures a lead (source=playbook, so a broker is
 * notified) and then downloads the real PDF straight from the site. We do NOT
 * email the PDF to the visitor (LoanLoop Resend is transactional broker/tenant
 * mail only). No em-dashes in any copy.
 */

type PlaybookKey = 'fhb' | 'refi'

interface Playbook {
  key: PlaybookKey
  eyebrow: string
  title: string
  shortName: string
  blurb: string
  inside: string[]
  pdf: string
  filename: string
}

const PLAYBOOKS: Playbook[] = [
  {
    key: 'fhb',
    eyebrow: 'First home buyers',
    title: 'The First Home Buyer Playbook',
    shortName: 'First Home Buyer Playbook',
    blurb:
      'A no-jargon guide to getting your first (or next) home loan right the first time. Written by our brokers, not a marketing team.',
    inside: [
      'How much you can actually borrow',
      'Which loan structure is right for you',
      'Whether you really need a 20% deposit',
      'What settlement really looks like',
    ],
    pdf: '/playbooks/greystone-fhb-playbook-2026.pdf',
    filename: 'Greystone First Home Buyer Playbook 2026.pdf',
  },
  {
    key: 'refi',
    eyebrow: 'Refinancing',
    title: 'The Refinancing Playbook',
    shortName: 'Refinancing Playbook',
    blurb:
      'How to close the loyalty gap, unlock your equity, and restructure your finance for what comes next, without leaving money on the table.',
    inside: [
      'The loyalty tax and what it is costing you',
      'Five triggers that mean it is time to refinance',
      'Unlocking equity and scaling a portfolio',
      'Rate cycle positioning: fix, split, or stay variable',
      'Costs, break-even and the full process',
      'Six mistakes that undermine a good refinance',
    ],
    pdf: '/playbooks/greystone-refinancing-playbook-2026.pdf',
    filename: 'Greystone Refinancing Playbook 2026.pdf',
  },
]

export default function PlaybookPage() {
  const [activeKey, setActiveKey] = useState<PlaybookKey | null>(null)
  const active = PLAYBOOKS.find((p) => p.key === activeKey) ?? null

  function handleDownload() {
    if (active) downloadAsset(active.pdf, active.filename)
  }

  return (
    <>
      <Seo
        title="Playbooks"
        path="/playbook"
        description="Free, practical property playbooks from our brokers: the First Home Buyer Playbook and the Refinancing Playbook. Download instantly."
      />
      <Nav />
      <main id="main" className="pt-36 pb-20">
        <section className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="text-xs font-semibold uppercase tracking-wider text-gs-text-muted mb-2">Free guides</div>
            <h1 className="font-heading text-3xl md:text-5xl text-ink font-semibold mb-4">Playbooks</h1>
            <p className="text-lg text-gs-text-soft max-w-2xl mx-auto">
              Practical, no-jargon guides written by our brokers. Enter your details and your PDF
              downloads instantly. No pressure, no obligation.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            {PLAYBOOKS.map((p) => (
              <div key={p.key} className="flex flex-col rounded-3xl border border-[#E8E8E2] bg-white p-8">
                <div className="text-xs font-semibold uppercase tracking-wider text-gs-text-muted mb-2">
                  {p.eyebrow}
                </div>
                <h2 className="font-heading text-2xl text-ink font-semibold mb-3">{p.title}</h2>
                <p className="text-gs-text-soft mb-5">{p.blurb}</p>
                <div className="text-xs font-semibold uppercase tracking-wide text-gs-text-muted mb-2">
                  What is inside
                </div>
                <ul className="text-sm text-gs-text-soft space-y-2 mb-8">
                  {p.inside.map((line) => (
                    <li key={line} className="flex items-start gap-2">
                      <span
                        className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0"
                        style={{ background: tenantConfig.accentColor }}
                      />
                      {line}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => setActiveKey(p.key)}
                  className="mt-auto px-6 py-3.5 rounded-lg font-semibold text-base w-full"
                  style={{ background: tenantConfig.accentColor, color: tenantConfig.primaryColor }}
                >
                  Download the Playbook
                </button>
                <p className="text-center text-xs text-gs-text-muted mt-3">Free PDF, updated for 2026 lender criteria.</p>
              </div>
            ))}
          </div>
        </section>
      </main>
      <Footer />

      <LeadCaptureModal
        open={active !== null}
        onClose={() => setActiveKey(null)}
        source="playbook"
        context={active ? { playbook: active.shortName } : undefined}
        heading={active ? `Get the ${active.shortName}` : undefined}
        blurb="Enter your details and your PDF downloads instantly. A broker will follow up, no obligation."
        submitLabel="Download the Playbook (PDF)"
        onSubmitted={handleDownload}
      />
    </>
  )
}
